import { FC } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, Typography } from "@mui/material";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { HomeIcon, LocationIcon } from "../../../../../assets/icons";
import FormNumericField from "../../../../../components/forms/FormNumericField";
import FormReadOnlyField from "../../../../../components/forms/FormReadOnlyField";
import FormSelect from "../../../../../components/forms/FormSelect";
import FormSwitchField from "../../../../../components/forms/FormSwitchField";
import {
	CarbonSnapFormData,
	DistanceUnit,
	TransportArc,
	TransportMode,
	TransportNode,
	WeightUnit,
} from "../../../../../datasources/carbonsnap/models";
import { Option } from "../../../../../datasources/responses/common";
import TransportModePicker from "./TransportModePicker";

interface Props {
	open: boolean;
	onClose: () => void;
	form: UseFormReturn<CarbonSnapFormData>;
	arcIndex: number;
	arc?: TransportArc;
	fromNode?: TransportNode;
	toNode?: TransportNode;
}

const vehicleTypeOptions: Option[] = [{ id: "placeholder", label: "Da definire" }];
const engineOptions: Option[] = [
	{ id: "placeholder1", label: "Benzina - euro 0" },
	{ id: "placeholder2", label: "Diesel - euro 1" },
	{ id: "placeholder3", label: "Elettrico" },
];
const payloadOptions: Option[] = [
	{ id: "1t", label: "1 t" },
	{ id: "2t", label: "2 t" },
	{ id: "3t", label: "3 t" },
];

interface VehicleDetailsSectionProps {
	form: UseFormReturn<CarbonSnapFormData>;
	arcIndex: number;
	vehiclePrefix: "firstMileVehicle" | "mainVehicle" | "lastMileVehicle";
	showAverageSaturation?: boolean;
}

const VehicleDetailsSection: FC<VehicleDetailsSectionProps> = ({
	form,
	arcIndex,
	vehiclePrefix,
	showAverageSaturation = false,
}) => {
	const { t } = useTranslation("feature.carbonsnap");
	const { t: tCommon } = useTranslation("common");
	const basePath = `arcs.${arcIndex}.${vehiclePrefix}` as const;

	return (
		<Stack spacing={2}>
			<Grid container spacing={2}>
				<Grid size={{ xs: 12, md: 6 }}>
					<FormSelect
						name={`${basePath}.vehicleTypeId` as any}
						label={t("arc/details/field/vehicle type")}
						form={form}
						options={vehicleTypeOptions}
						fullWidth
						size="small"
						rules={{
							required: tCommon("this field is required"),
						}}
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 6 }}>
					<FormSelect
						name={`${basePath}.engineId` as any}
						label={t("arc/details/field/engine")}
						form={form}
						options={engineOptions}
						fullWidth
						size="small"
						rules={{
							required: tCommon("this field is required"),
						}}
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 6 }}>
					<FormNumericField
						name={`${basePath}.returnEmptyPercent` as any}
						label={t("arc/details/field/empty return")}
						form={form}
						fullWidth
						minFractionDigits={0}
						maxFractionDigits={2}
						size="small"
						rules={{
							required: tCommon("this field is required"),
							min: { value: 0, message: t("arc/details/validation/min 0") },
							max: { value: 100, message: t("arc/details/validation/max 100") },
						}}
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 6 }}>
					<FormSelect
						name={`${basePath}.payloadId` as any}
						label={t("arc/details/field/payload")}
						form={form}
						options={payloadOptions}
						fullWidth
						size="small"
						rules={{
							required: tCommon("this field is required"),
						}}
					/>
				</Grid>
				{showAverageSaturation && (
					<Grid size={{ xs: 12, md: 6 }}>
						<FormNumericField
							name={`${basePath}.averageSaturationPercent` as any}
							label={t("arc/details/field/average saturation")}
							form={form}
							fullWidth
							minFractionDigits={0}
							maxFractionDigits={2}
							size="small"
							rules={{
								required: tCommon("this field is required"),
								min: { value: 0, message: t("arc/details/validation/min 0") },
								max: { value: 100, message: t("arc/details/validation/max 100") },
							}}
						/>
					</Grid>
				)}
				<Grid size={{ xs: 12 }}>
					<FormSwitchField
						name={`${basePath}.ownedVehicle` as any}
						label={t("arc/details/field/owned vehicle")}
						form={form}
					/>
				</Grid>
			</Grid>
		</Stack>
	);
};

const ArcDetailsDialog: FC<Props> = ({ open, onClose, form, arcIndex, arc, fromNode, toNode }) => {
	const { t } = useTranslation("feature.carbonsnap");
	const { t: tCommon } = useTranslation("common");
	const showDistance = arc ? !arc.networkEnabled : false;
	const networkEnabled = arc?.networkEnabled ?? false;

	const weightUnitOptions: Option[] = [
		{ id: WeightUnit.Tonne, label: t("arc/details/unit/tonnes") },
		{ id: WeightUnit.Kg, label: t("arc/details/unit/kilograms") },
		{ id: WeightUnit.Teu, label: t("arc/details/unit/teu") },
	];

	const distanceUnitOptions: Option[] = [
		{ id: DistanceUnit.Km, label: t("arc/details/unit/kilometers") },
		{ id: DistanceUnit.Meter, label: t("arc/details/unit/meters") },
		{ id: DistanceUnit.Mile, label: t("arc/details/unit/miles") },
		{ id: DistanceUnit.NauticalMile, label: t("arc/details/unit/nautical miles") },
	];

	const handleSave = async () => {
		const isValid = await form.trigger(`arcs.${arcIndex}` as any);
		if (isValid) {
			onClose();
		}
	};

	const handleModeSelection = (mode: TransportMode) => {
		form.setValue(`arcs.${arcIndex}.transportMode`, mode);
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle>{t("arc/details/title")}</DialogTitle>
			<DialogContent dividers>
				<Stack spacing={3}>
					<Stack spacing={1.5}>
						<Typography variant="subtitle2" color="text.secondary">
							{t("arc/details/section/route")}
						</Typography>
						<Box sx={{ display: "flex", gap: 2 }}>
							<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", pt: 0.5 }}>
								<Box
									sx={{
										width: 32,
										height: 32,
										borderRadius: "50%",
										border: 2,
										borderColor: "primary.dark",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										backgroundColor: "background.paper",
									}}
								>
									<HomeIcon fontSize="small" sx={{ color: "primary.dark" }} />
								</Box>
								<Box
									sx={{
										width: 2,
										flex: 1,
										backgroundColor: "primary.dark",
										minHeight: 40,
										my: 0.5,
									}}
								/>
								<Box
									sx={{
										width: 32,
										height: 32,
										borderRadius: "50%",
										border: 2,
										borderColor: "primary.dark",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										backgroundColor: "background.paper",
									}}
								>
									<LocationIcon fontSize="small" sx={{ color: "primary.dark" }} />
								</Box>
							</Box>
							<Stack spacing={2} sx={{ flex: 1 }}>
								<FormReadOnlyField
									label={t("arc/details/field/departure")}
									fullWidth
									value={fromNode?.address || t("arc/details/field/select node")}
								/>
								<FormReadOnlyField
									label={t("arc/details/field/arrival")}
									fullWidth
									value={toNode?.address || t("arc/details/field/select node")}
								/>
							</Stack>
						</Box>
					</Stack>

					<Grid container spacing={2}>
						<Grid size={{ xs: 12, md: 6 }}>
							<FormNumericField
								name={`arcs.${arcIndex}.weight`}
								label={t("arc/details/field/cargo weight")}
								form={form}
								fullWidth
								minFractionDigits={0}
								maxFractionDigits={2}
								size="small"
								rules={{
									required: tCommon("this field is required"),
									min: { value: 0.01, message: t("arc/details/validation/min 0.01") },
								}}
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 6 }}>
							<FormSelect
								name={`arcs.${arcIndex}.weightUnit`}
								label={t("arc/details/field/weight unit")}
								form={form}
								options={weightUnitOptions}
								fullWidth
								size="small"
								rules={{
									required: tCommon("this field is required"),
								}}
							/>
						</Grid>

						{showDistance && (
							<>
								<Grid size={{ xs: 12, md: 6 }}>
									<FormNumericField
										name={`arcs.${arcIndex}.distance`}
										label={t("arc/details/field/distance")}
										form={form}
										fullWidth
										minFractionDigits={0}
										maxFractionDigits={2}
										size="small"
										rules={{
											validate: (value) => {
												if (value === null || value === undefined || value === "") {
													return true;
												}
												const numValue = typeof value === "number" ? value : Number(value);
												return (
													(numValue > 0 && !isNaN(numValue)) ||
													t("arc/details/validation/min 0.01")
												);
											},
										}}
									/>
								</Grid>
								<Grid size={{ xs: 12, md: 6 }}>
									<FormSelect
										name={`arcs.${arcIndex}.distanceUnit`}
										label={t("arc/details/field/distance unit")}
										form={form}
										options={distanceUnitOptions}
										fullWidth
										size="small"
									/>
								</Grid>
							</>
						)}
					</Grid>

					<Stack spacing={3}>
						{networkEnabled ? (
							<>
								<Stack spacing={2}>
									<Typography variant="subtitle2" color="text.secondary">
										{t("arc/details/section/first mile")}
									</Typography>
									<VehicleDetailsSection
										form={form}
										arcIndex={arcIndex}
										vehiclePrefix="firstMileVehicle"
										showAverageSaturation={true}
									/>
								</Stack>

								<Stack spacing={2}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<Typography variant="subtitle2" color="text.secondary">
											{t("arc/details/section/main transport")}
										</Typography>
										<InfoOutlinedIcon fontSize="small" color="action" />
									</Box>
									<TransportModePicker value={arc?.transportMode} onChange={handleModeSelection} />
									<VehicleDetailsSection
										form={form}
										arcIndex={arcIndex}
										vehiclePrefix="mainVehicle"
										showAverageSaturation={false}
									/>
								</Stack>

								{/* Ultimo miglio */}
								<Stack spacing={2}>
									<Typography variant="subtitle2" color="text.secondary">
										{t("arc/details/section/last mile")}
									</Typography>
									<VehicleDetailsSection
										form={form}
										arcIndex={arcIndex}
										vehiclePrefix="lastMileVehicle"
										showAverageSaturation={true}
									/>
								</Stack>
							</>
						) : (
							<Stack spacing={2}>
								<Typography variant="subtitle2" color="text.secondary">
									{t("arc/details/section/main transport")}
								</Typography>
								<VehicleDetailsSection
									form={form}
									arcIndex={arcIndex}
									vehiclePrefix="mainVehicle"
									showAverageSaturation={false}
								/>
							</Stack>
						)}
					</Stack>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" onClick={onClose}>
					{t("arc/details/button/cancel")}
				</Button>
				<Button variant="contained" onClick={handleSave}>
					{t("arc/details/button/save")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ArcDetailsDialog;
